import type { DragEvent } from "react";
import "./layout.css";

export interface StageFunnelSegment {
  value: string;
  label: string;
  count: number;
  /** True for a terminal "lost" stage (disqualified, closed lost) — shown in red, not green. */
  negative?: boolean;
}

export interface StageFunnelProps {
  segments: StageFunnelSegment[];
  onSegmentDrop?: (value: string, event: DragEvent<HTMLDivElement>) => void;
  onSegmentDragEnter?: (value: string, event: DragEvent<HTMLDivElement>) => void;
}

const MIN_TINT = 0.4;

function segmentColor(segment: StageFunnelSegment, progressIndex: number, progressSteps: number): string {
  if (segment.negative) return "var(--color-danger)";
  const step = progressSteps > 1 ? progressIndex / (progressSteps - 1) : 1;
  const tint = MIN_TINT + step * (1 - MIN_TINT);
  return `color-mix(in srgb, var(--color-success) ${Math.round(tint * 100)}%, var(--color-success-soft))`;
}

/** Chevron flow strip shown above a Kanban board — one arrow per column, aligned to its width. */
export function StageFunnel({ segments, onSegmentDrop, onSegmentDragEnter }: StageFunnelProps) {
  const total = segments.reduce((sum, segment) => sum + segment.count, 0);
  if (total === 0) return null;
  const progressSegments = segments.filter((segment) => !segment.negative);
  return (
    <div className="ui-stage-funnel" role="list" aria-label="Stage flow">
      {segments.map((segment, index) => {
        const progressIndex = progressSegments.indexOf(segment);
        return (
          <div
            key={segment.value}
            role="listitem"
            title={`${segment.label}: ${segment.count}`}
            className={`ui-stage-funnel__segment${index === 0 ? " is-first" : ""}`}
            onDragOver={onSegmentDrop ? (event) => event.preventDefault() : undefined}
            onDragEnter={onSegmentDragEnter ? (event) => onSegmentDragEnter(segment.value, event) : undefined}
            onDrop={
              onSegmentDrop
                ? (event) => {
                    event.preventDefault();
                    onSegmentDrop(segment.value, event);
                  }
                : undefined
            }
            style={{ background: segmentColor(segment, progressIndex, progressSegments.length) }}
          />
        );
      })}
    </div>
  );
}
